import { useState } from 'react';

import { Button, Logo, Phone, Result, Space } from '@bookora/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { logout } from '@/api/endpoints/auth';
import { LinkButton } from '@/components/link-button';

import { useAuth, useSessionStore } from './token-store';

/** The platform console, where a super-admin's work actually is. */
const ADMIN_CONSOLE_URL = String(
  import.meta.env.VITE_ADMIN_URL ?? 'https://admin.bookora.net',
).replace(/\/+$/, '');

/**
 * Route guard for everything behind the shell.
 *
 * Signed out → `/login?next=…`, so a bookmarked patient file returns the user
 * to that file after they authenticate.
 *
 * Signed in with **no clinic membership** → a welcome card, unless the account
 * is a super-admin (which belongs on the platform console — that branch gets a
 * card with a link there). No membership is a real state for a normal account:
 * an owner who verified their phone but never created a clinic, a staff member
 * whose only membership was deactivated, or someone who signed in with the
 * wrong number. The card leads with the happy path — «ساخت کلینیک», which opens
 * `/signup` — and keeps the staff-invite hint and «ورود با شمارهٔ دیگه» as a
 * quiet second line. That hint lives HERE, never on `/signup`: this is where
 * someone who came to log in arrives, while the signup page talks only to
 * owners. Without the card they would land on a dashboard where every request
 * 403s with `CLINIC_CONTEXT_MISSING`, which reads like an outage rather than an
 * account question.
 *
 * This is a routing convenience, not a security boundary. Every endpoint the
 * screens behind it call is independently authenticated and permission-checked.
 */
export function AuthGate() {
  const { isAuthenticated, memberships, user } = useAuth();
  const clearSession = useSessionStore((state) => state.clearSession);
  const location = useLocation();

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    const suffix = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
    return <Navigate to={`/login${suffix}`} replace />;
  }

  if (memberships.length === 0) {
    if (!user?.isSuperAdmin) return <NoClinicWelcome phone={user?.phone ?? null} />;

    // A super-admin belongs on the platform console, not here — a dead end with
    // an explanation. On the sign-in ground, centred: there is no shell around
    // this card, and a lone result pinned to the top of a blank page read as a
    // broken load.
    return (
      <div className="bk-login">
        <Result
          className="bk-login__card"
          status="warning"
          title="هنوز عضو هیچ کلینیکی نیستید"
          subTitle="این حساب مربوط به مدیریت بوکوراست و به پنل کلینیک دسترسی نداره. وارد پنل مدیریت بوکورا بشید."
          extra={
            <Space wrap>
              <Button type="primary" href={ADMIN_CONSOLE_URL}>
                رفتن به پنل مدیریت
              </Button>
              <Button
                onClick={() => {
                  clearSession();
                  window.location.assign('/login');
                }}
              >
                خروج از حساب
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  return <Outlet />;
}

/**
 * A signed-in account with no clinic yet. Most are owners who stopped before
 * naming their clinic, so the one primary action continues that. The staff
 * hint is secondary and gentle: a fact about how staff join, never an error.
 */
function NoClinicWelcome({ phone }: { phone: string | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refreshToken = useSessionStore((state) => state.refreshToken);
  const clearSession = useSessionStore((state) => state.clearSession);
  const [signingOut, setSigningOut] = useState(false);

  /** A real sign-out: revoke the refresh token first, then drop the local session. */
  async function switchNumber(): Promise<void> {
    setSigningOut(true);
    try {
      if (refreshToken) await logout(refreshToken);
    } catch {
      // The local session is dropped either way; the token expires on its own.
    } finally {
      clearSession();
      queryClient.clear();
      setSigningOut(false);
      void navigate('/login', { replace: true });
    }
  }

  return (
    <div className="bk-login">
      <Result
        className="bk-login__card"
        icon={<Logo variant="mark" size={48} />}
        title="به بوکورا خوش اومدید"
        subTitle="برای شروع، کلینیک یا مطب‌تون رو بسازید."
        extra={
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <LinkButton type="primary" size="large" to="/signup">
              ساخت کلینیک
            </LinkButton>
            <div className="bk-subtle bk-login__fine-print">
              کارمند کلینیک هستید؟ از مدیر کلینیک بخواید شما رو
              {phone ? (
                <>
                  {' '}
                  با شمارهٔ <Phone value={phone} />
                </>
              ) : null}{' '}
              به کارکنان اضافه کنه، بعد دوباره وارد بشید.
              <div>
                <Button
                  type="link"
                  size="small"
                  loading={signingOut}
                  onClick={() => void switchNumber()}
                >
                  ورود با شمارهٔ دیگه
                </Button>
              </div>
            </div>
          </Space>
        }
      />
    </div>
  );
}
